<script setup>
import { changeShippingMethod } from 'src/api/checkout';
import GoogleMapDialog from 'src/components/GoogleMapDialog.vue';
import { defineProps, ref, inject, computed, defineExpose } from 'vue';
import { useIns, useStore } from 'src/utils/v2_helper/common';
import { useLoading } from 'src/utils/hooks/loading';
import ChooseService from './ChooseService.vue';
import ManualTable from './ManualTable.vue';
import User from '../../full_dine_in/components/UserInfo.vue';

const app = useIns();
const store = useStore();
const { loading, hide } = useLoading();

const props = defineProps({
  checkoutOption: {
    type: Object,
    default: () => ({}),
  },
});

const refresh = inject('refresh');
const resetState = inject('resetState');
const refreshShippingPrice = inject('refreshShippingPrice');

const userRef = ref();
const serviRef = ref();
const mulRef = ref();
const mapRef = ref();

const addressInfo = computed(() => store.state.Menu.addressInfo);
const hadAddress = computed(() => {
  const { latitude, longitude } = addressInfo.value || {};
  return latitude && longitude;
});
const isChooseAddress = computed(() => addressInfo.value.contactMechId);
const deliveryValid = computed(
  () => hadAddress.value && !!isChooseAddress.value,
);
const userValid = computed(() => userRef.value?.userValid);
const fillFormValid = computed(() => userRef.value?.fillFormValid);
const invalidTable = computed(() => mulRef.value?.invalidTable);

// ==== funs ====
function getUser() {
  return userRef.value.userForm;
}
function getFillInfo() {
  return userRef.value.fillForm;
}
function focusBuyerInfo(errorKey) {
  userRef.value?.focusBuyerInfo(errorKey);
}
function getTable() {
  return mulRef.value?.tableNumber;
}
function onCheckTime() {
  return serviRef.value.onCheckTime();
}
function onShowMap() {
  mapRef.value.show = true;
}
// 地址check失败回调
function onCheckFaild() {
  const info = { ...addressInfo.value };
  info.contactMechId = '';
  store.dispatch('Menu/setAddressInfo', info);
}
// AI-assisted 2026-09 START: Reset shipping before refreshing the selected service.
async function onConfirm({ from, to }) {
  loading();
  changeShippingMethod({
    shippingMethod: 'STANDARD@_NA_',
    contactMechId: props.checkoutOption.contactMechId,
  }).then(() => {
    hide();
    resetState();
    refresh().then(() => {
      if (from === to && to === 'DELIVERY') {
        refreshShippingPrice();
      }
    });
  });
}
// AI-assisted 2026-09 END

defineExpose({
  invalidTable,
  userValid,
  fillFormValid,
  deliveryValid,
  getUser,
  getFillInfo,
  focusBuyerInfo,
  getTable,
  onCheckTime,
  onCheckFaild,
});
</script>

<template>
  <div class="user">
    <section class="user-info">
      <User ref="userRef" />
    </section>
    <q-separator />
    <ChooseService ref="serviRef" class="user-service" @onConfirm="onConfirm" />
    <!-- <q-separator /> -->
    <!--桌号输入和桌号选择-->
    <section
      v-if="isDine && (isNormalDineIn || isRoomService)"
      class="user-table"
    >
      <ManualTable ref="mulRef" />
    </section>
    <!--地址信息模块-->
    <div v-if="isDelivery" class="user-address">
      <div class="user-address-head">
        <span> {{ $t('checkout.shippingAddress') }}</span>
        <span @click="onShowMap">{{ $t('checkout.change') }}</span>
      </div>
      <div v-if="addressInfo.address3" class="user-address-content">
        {{ addressInfo.address3 }}
        <div class="content-detail">{{ addressInfo.address1 || '' }}</div>
      </div>
      <div v-if="addressInfo.address2" class="user-address-tag" maxlength="50">
        {{ addressInfo.address2 }}
      </div>
      <div v-if="!hadAddress" class="user-address-addbtn">
        <span class="empty-text">{{ $t('checkout.pleaseFillAddr') }}</span>
        <div class="btn" @click="onShowMap">
          {{ $t('checkout.addAddress') }}
        </div>
      </div>
      <!-- 这里需要加判断 UserInfo -->
      <template v-if="!isChooseAddress && hadAddress">
        <div class="user-address-confirm" @click="onShowMap">
          {{ $t('checkout.confirmAddress') }}
        </div>
        <div class="user-address-tip">
          <q-img
            src="~assets/icons/svg/icon/warningPopup.svg"
            class="tip-icon"
          />
          {{ $t('checkout.confirmTip') }}
        </div>
      </template>
      <GoogleMapDialog ref="mapRef" @checkFaild="onCheckFaild" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user {
  padding: 0;
  font-family: Montserrat;
  &-address {
    padding: 16px;
    &-addbtn {
      .empty-text {
        color: #6b7280;
        font-size: 12px;
      }
      .btn {
        margin-top: 6px;
        height: 40px;
        line-height: 40px;
        background: $primary;
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        color: #fff;
        font-weight: bold;
        text-align: center;
      }
    }
    &-head {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      font-weight: 800;
      span:nth-child(2) {
        color: $primary;
      }
    }
    &-content {
      font-size: 16px;
      padding: 8px 0;
      .content-detail {
        margin: 6px 0;
      }
    }
    &-tag {
      font-size: 12px;
      display: inline-block;
      padding: 4px 8px;
      font-weight: 400;
      color: $dark;
      background: #f9fafb;
      border: 1px solid $dark;
      border-radius: 4px;
      text-align: center;
    }
    &-confirm {
      height: 20px;
      font-size: 16px;
      font-weight: bold;
      color: $primary;
      margin-bottom: 10px;
      & > .q-btn {
        box-shadow: none !important;
        background: none;
      }
    }
    &-tip {
      font-size: 12px;
      font-weight: 400;
      color: $negative;
      display: flex;
      align-items: center;
      & > span {
        padding-left: 5px;
      }
      .tip-icon {
        width: 16px;
        height: 16px;
        margin-right: 5px;
      }
    }
  }
  &-table {
    padding: 8px 16px;
  }
  &-form {
    padding: 16px;
    &-title {
      font-size: 12px;
      font-weight: bold;
      margin: 12px 0 8px;
    }
    &-err {
      font-size: 12px;
      line-height: 120%;
      color: $negative;
      margin-top: 8px;
    }
  }
  &-info {
  }
  &-service {
    padding: 16px;
  }
}
</style>

<!--Cover Quasar Style-->
<style lang="scss" scoped>
.input {
  :deep(.q-field__control) {
    border: 1px solid $negative;
    color: transparent;
    box-shadow: none !important;
    height: 36px;
  }
  :deep(.q-field__control:before) {
    border: none;
  }
}
</style>
